import {useEffect, useRef, useState} from 'react'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import ChatBot from 'react-simple-chatbot'
import {mainSteps} from './steps.js'
import {useTranslation} from 'react-i18next'

import {ChevronDownIcon, XCloseIcon} from '@components/ui/icon.js'
import {Spinner} from '@components/ui/spinner.js'
import {useChatbotStore} from './store/chatbotStore.js'
import {useMe} from '../user/queries/useMe.js'
import {cn, raise} from '@/utilities/helpers.js'
import {useURLParsedParams} from '@/hooks/useURLParsedParams.js'
import {AssignmentUrlParams} from '../assignment/types.js'
import {ChatbotStep} from '@features/chatbot/types.ts'
import {ChatBotIcon} from './components/chatbot-icons.js'

export const ChatBotWrapper = () => {
    const {i18n, t} = useTranslation()
    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const code = urlParams.success ? urlParams.data.code : undefined
    const userMeQuery = useMe({throwOnError: true})
    const currentUser = userMeQuery.data ?? raise('user is nullish')
    //chatbot
    const setChatbotRef = useChatbotStore(state => state.setChatbotRef)
    const currentAssignment = useChatbotStore(state => state.assignment)
    const resetChatHistory = useChatbotStore(state => state.resetChatHistory)
    const resetSelectedAssignment = useChatbotStore(state => state.resetSelectedAssignment)
    const [botOpened, setBotOpened] = useState(false)
    const [helpTextVisible, setHelpTextVisible] = useState(true)
    const [botSteps, setBotSteps] = useState<ChatbotStep[] | null>(null)
    const [resetChatbot, setResetChatbot] = useState(false)
    const [hasInitialized, setHasInitialized] = useState(false)
    const chatbot = useRef()
    const chatbotInput = document.getElementById('chatbotinput')
    //local state
    const isInShootPage = code !== undefined
    const assignmentStatus = currentAssignment?.status
    const taskStatus = currentAssignment?.task_status.status

    useEffect(() => {
        if (isInShootPage && !currentAssignment) {
            return
        }

        if (hasInitialized && currentAssignment) {
            return
        }

        setChatbotRef(chatbot)
        setResetChatbot(true)
        setTimeout(() => setResetChatbot(false), 1000)
        resetChatHistory()
        resetSelectedAssignment()
        setBotSteps(
            mainSteps({
                currentUser,
                isInShootPage,
                shoot: currentAssignment,
                assignmentStatus,
                taskStatus
            }) as ChatbotStep[]
        )
        setHasInitialized(true)
    }, [currentAssignment])

    useEffect(() => {
        setHasInitialized(false)
    }, [i18n.language])

    useEffect(() => {
        chatbotInput?.addEventListener('keydown', e => {
            if (e.code === 'Enter') e.preventDefault()
        })
    }, [chatbotInput])

    // Auto-scroll to bottom when new messages appear
    useEffect(() => {
        if (botOpened && chatbot.current && !resetChatbot) {
            const scrollToBottom = () => {
                const chatbotContent = document.querySelector('.rsc-content')
                if (chatbotContent) {
                    const isAtBottom =
                        chatbotContent.scrollTop + chatbotContent.clientHeight >= chatbotContent.scrollHeight - 10
                    // Always scroll for new messages, or if user is already near the bottom
                    if (isAtBottom || chatbotContent.scrollHeight > chatbotContent.clientHeight) {
                        chatbotContent.scrollTo({
                            top: chatbotContent.scrollHeight,
                            behavior: 'smooth'
                        })
                    }
                }
            }

            let lastMessageCount = 0

            // Function to check for new messages
            const checkForNewMessages = () => {
                const messages = document.querySelectorAll('.rsc-content .rsc-ts, .rsc-content .rsc-cs')
                const currentMessageCount = messages.length

                if (currentMessageCount > lastMessageCount) {
                    lastMessageCount = currentMessageCount
                    setTimeout(scrollToBottom, 150)
                }
            }

            // Set up a MutationObserver to watch for DOM changes
            const observer = new MutationObserver(mutations => {
                let shouldCheck = false
                mutations.forEach(mutation => {
                    if (mutation.type === 'childList') {
                        // Check if any nodes were added
                        if (mutation.addedNodes.length > 0) {
                            shouldCheck = true
                        }
                        // Also check if any existing nodes were modified
                        if (
                            mutation.target instanceof Element &&
                            (mutation.target.classList.contains('rsc-content') ||
                                mutation.target.closest('.rsc-content'))
                        ) {
                            shouldCheck = true
                        }
                    }
                })

                if (shouldCheck) {
                    checkForNewMessages()
                }
            })

            // Also set up a polling mechanism as fallback
            const pollInterval = setInterval(checkForNewMessages, 500)

            const chatbotContent = document.querySelector('.rsc-content')
            if (chatbotContent) {
                observer.observe(chatbotContent, {
                    childList: true,
                    subtree: true,
                    attributes: false,
                    attributeOldValue: false
                })

                // Initial count and scroll
                setTimeout(() => {
                    const messages = document.querySelectorAll('.rsc-content .rsc-ts, .rsc-content .rsc-cs')
                    lastMessageCount = messages.length
                    scrollToBottom()
                }, 500)
            }

            return () => {
                observer.disconnect()
                clearInterval(pollInterval)
            }
        }
    }, [botOpened, resetChatbot])

    const onBotToggle = () => {
        setBotOpened(!botOpened)
        setHelpTextVisible(false)
    }

    return (
        <>
            <div
                className={cn(
                    'fixed top-0 bottom-0 right-0 md:bottom-20 md:right-6 md:top-[initial] z-dropdown-overlay origin-bottom-right rounded-none! md:rounded-[10px] shadow-[rgb(0_0_0/15%)_0_12px_24px_0] transition duration-200 ease-in-out',
                    'chatbot-wrapper',
                    botOpened ? 'scale-100' : 'scale-0'
                )}
            >
                {botSteps &&
                    (resetChatbot ? (
                        <></>
                    ) : (
                        <ChatBot
                            key={`${botSteps.length}-${currentAssignment?.id}`}
                            steps={botSteps}
                            ref={chatbot}
                            isTextarea
                            cache={false}
                            floating={undefined}
                            invalid={undefined}
                            inputAttributes={{id: 'chatbotinput'}}
                            placeholder={t('chatbot:chatbot_input:placeholder')}
                            hideUserAvatar
                            botAvatar="https://ca.slack-edge.com/T03RF4G89-U01NZ7UHYCQ-9f63803fcc00-512"
                            headerComponent={
                                <div className="flex items-center justify-between fill-white bg-[#00535a] text-white text-md font-semibold h-14 px-2.5 [&_svg]:w-6 [&_svg]:fill-white! [&_svg]:cursor-pointer">
                                    <XCloseIcon onClick={() => setBotOpened(false)} />
                                    <p>{t('chatbot:header')}</p>
                                    <span />
                                </div>
                            }
                            className="h-dvh md:h-170 md:w-94 w-dvw rounded-none md:rounded-[10px] bg-white font-sans"
                            contentStyle={{height: 'calc(100% - 110px)'}}
                            footerStyle={{height: '110px'}}
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 'inherit'
                            }}
                        />
                    ))}
            </div>

            <div
                className={cn(
                    'animate-in fade-in duration-200 fixed bottom-20 right-6 h-10 z-30 bg-white flex items-center gap-3 py-1 px-4 rounded-full text-sm shadow-[0_0_32px_0_rgba(143,143,143,0.45)] [&_svg]:cursor-pointer [&_svg]:relative max-md:hidden',
                    !helpTextVisible && 'animate-out fade-out opacity-0 pointer-events-none'
                )}
            >
                <span>{t('chatbot:message')}</span>
                <XCloseIcon
                    size={16}
                    width={16}
                    height={16}
                    onClick={() => {
                        setHelpTextVisible(false)
                    }}
                />
            </div>
            <div
                onClick={onBotToggle}
                className="flex items-center justify-center cursor-pointer bg-[#00535a] bottom-4 rounded-full shadow-[0_12px_24px_0_rgba(0,0,0,0.15)] size-14 fixed right-6 scale-100 transition duration-200 ease-in-out z-[var(--z-index-dropdown-menu)] [&_svg]:w-[30px] [&_svg]:h-[30px] [&_svg]:fill-white max-md:right-4 max-md:bottom-6"
            >
                {resetChatbot ? (
                    <Spinner size={16} color={'var(--color-white)'} />
                ) : botOpened ? (
                    <ChevronDownIcon className="w-5! fill-white" />
                ) : (
                    <ChatBotIcon />
                )}
            </div>
        </>
    )
}
